import React from 'react';
import { Box, Typography, Button, Stack, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { styled, keyframes } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import CampaignIcon from '@mui/icons-material/Campaign';
import SearchIcon from '@mui/icons-material/Search';
import MusicNoteIcon from '@mui/icons-material/MusicNote';

// ─── Animations ──────────────────────────────────────────────────────────────

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50%       { transform: translateY(-16px) rotate(2deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const goldShimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageWrap = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(145deg, #3b0764 0%, #831843 55%, #7c2d12 100%)'
    : 'linear-gradient(145deg, #a78bfa 0%, #ec4899 55%, #f97316 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px 16px',
}));

const Card = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(0, 0, 0, 0.3)'
    : 'rgba(255,255,255,0.15)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  borderRadius: 32,
  padding: '72px 68px 68px',
  maxWidth: 780,
  width: '100%',
  boxShadow: '0 32px 80px rgba(0,0,0,0.20)',
  textAlign: 'center',
  animation: `${fadeUp} 0.55s cubic-bezier(0.22, 1, 0.36, 1)`,
}));

const Illustration = styled(Box)(() => ({
  fontSize: 140,
  display: 'inline-block',
  animation: `${float} 3.5s ease-in-out infinite`,
  filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))',
  lineHeight: 1,
  marginBottom: 4,
}));

const Badge = styled(Box)(() => ({
  display: 'inline-block',
  background: 'linear-gradient(135deg, #f97316, #ec4899)',
  color: 'white',
  fontWeight: 800,
  fontSize: '0.8rem',
  letterSpacing: 2,
  borderRadius: 99,
  padding: '4px 14px',
  marginBottom: 16,
}));

// ─── Component ────────────────────────────────────────────────────────────────

const NotFound = () => (
  <PageWrap>
    <Card>

      {/* Illustration */}
      <Illustration>📡</Illustration>

      {/* Small 404 badge */}
      <Box>
        <Badge>ERROR 404</Badge>
      </Box>

      {/* Headline */}
      <Typography
        variant="h4"
        sx={{ fontWeight: 800, color: '#ffffff', mb: 1.5, fontSize: { xs: '2.2rem', sm: '3.2rem' }, lineHeight: 1.2 }}
      >
        Well, this is awkward.
      </Typography>

      {/* Clear explanation */}
      <Typography
        variant="body1"
        sx={{ color: 'rgba(255,255,255,0.85)', mb: 1, fontSize: { xs: '1.2rem', sm: '1.5rem' }, lineHeight: 1.7 }}
      >
        The page you're looking for doesn't exist — maybe it went viral and got deleted.
        Either way, we've got better things ahead.
      </Typography>

      {/* Recovery message */}
      <Typography
        variant="body2"
        sx={{ color: 'rgba(255,255,255,0.6)', mb: 3.5, fontSize: '1.2rem' }}
      >
        While we fix the signal, here's where to go next 👇
      </Typography>

      {/* Primary CTA */}
      <Button
        component={RouterLink}
        to="/"
        variant="contained"
        fullWidth
        startIcon={<HomeIcon />}
        sx={{
          background: 'linear-gradient(135deg, #a78bfa, #ec4899)',
          color: 'white',
          fontWeight: 700,
          fontSize: '1.3rem',
          borderRadius: 3,
          py: 1.9,
          mb: 1.5,
          textTransform: 'none',
          boxShadow: '0 6px 20px rgba(167,139,250,0.45)',
          '&:hover': {
            background: 'linear-gradient(135deg, #7c3aed, #db2777)',
            boxShadow: '0 8px 24px rgba(167,139,250,0.55)',
          },
        }}
      >
        Take Me Home
      </Button>

      {/* Secondary CTAs */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/register"
          variant="outlined"
          fullWidth
          startIcon={<CampaignIcon />}
          sx={{
            borderColor: 'rgba(255,255,255,0.5)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '1.2rem',
            borderRadius: 3,
            py: 1.7,
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: '#ffffff' },
          }}
        >
          Hire an Influencer
        </Button>
        <Button
          component={RouterLink}
          to="/register"
          variant="outlined"
          fullWidth
          startIcon={<SearchIcon />}
          sx={{
            borderColor: 'rgba(255,255,255,0.5)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '1.2rem',
            borderRadius: 3,
            py: 1.7,
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: '#ffffff' },
          }}
        >
          Get Brand Deals
        </Button>
      </Stack>

      {/* Divider */}
      <Divider sx={{ mb: 2.5, borderColor: 'rgba(255,255,255,0.25)' }}>
        <Typography variant="caption" sx={{
          fontWeight: 700,
          letterSpacing: 2,
          textTransform: 'uppercase',
          fontSize: '1rem',
          background: 'linear-gradient(90deg, #b8860b 0%, #ffd700 30%, #fffacd 55%, #ffd700 75%, #b8860b 100%)',
          backgroundSize: '250% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          animation: `${goldShimmer} 3s linear infinite`,
        }}>
          Where Creators Meet Brands
        </Typography>
      </Divider>

      {/* Platform icons */}
      <Stack direction="row" spacing={4} justifyContent="center" alignItems="center">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <InstagramIcon sx={{ fontSize: 40, color: '#E1306C' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Instagram</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <YouTubeIcon sx={{ fontSize: 40, color: '#FF0000' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>YouTube</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <MusicNoteIcon sx={{ fontSize: 40, color: '#69C9D0' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>TikTok</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: 34, lineHeight: 1, color: 'rgba(255,255,255,0.9)' }}>𝕏</Typography>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Twitter / X</Typography>
        </Box>
      </Stack>

    </Card>
  </PageWrap>
);

export default NotFound;
